import React from "react";
import { Link } from "react-router-dom";
import { Shield, Heart, Users, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PAID = ["member", "premium", "vip"];

const HERO_BG = "https://images.pexels.com/photos/9146373/pexels-photo-9146373.jpeg";

export default function Landing() {
  const { user } = useAuth();
  const isMember = user && (user.role === "admin" || PAID.includes(user.membership_tier));
  return (
    <div className="bg-[#110D0D]" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 tt-fade-up">
            <div className="text-xs uppercase tracking-[0.28em] text-[#C93B3B] mb-6" data-testid="hero-eyebrow">
              A Private Wellness Membership
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#F7ECEB]">
              Therapeutic touch,<br />
              <span className="italic text-[#C93B3B]">held with reverence.</span>
            </h1>
            <p className="mt-8 text-lg text-[#A89896] max-w-xl leading-relaxed">
              Touche.Today connects members with certified cuddle therapists and offers intimacy coaching with our founder — quietly, professionally, and always on your terms.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/membership" className="btn-primary" data-testid="hero-cta-membership">
                Explore Membership
              </Link>
              <Link to="/practitioners" className="btn-ghost" data-testid="hero-cta-practitioners">
                Meet Our Practitioners
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8 text-sm text-[#A89896]">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#C93B3B]" /> Fully vetted</div>
              <div className="flex items-center gap-2"><Heart className="w-4 h-4 text-[#C93B3B]" /> Consent-first</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#C93B3B]" /> Private</div>
            </div>
          </div>
          <div className="lg:col-span-6 tt-fade-up tt-delay-2">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(44,42,38,0.10)]">
              <img src={HERO_BG} alt="A serene wellness session" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#110D0D]/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-xs uppercase tracking-[0.24em] opacity-90">Discretion promised</div>
                <div className="font-serif text-2xl mt-1">Every session is a sanctuary.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Shield, title: "Certified & Vetted", body: "Every practitioner completes rigorous training in trauma-informed, consent-forward touch." },
            { icon: Heart, title: "Sensory Wellness", body: "From weighted blankets to aromatherapy — we curate every element of your session." },
            { icon: Sparkles, title: "Founder Coaching", body: "Premium and VIP members book 1:1 intimacy coaching sessions with our founder." },
          ].map((p, i) => (
            <div key={p.title} className={`tt-card p-8 tt-fade-up`} style={{ animationDelay: `${i * 100}ms` }} data-testid={`pillar-${i}`}>
              <p.icon className="w-6 h-6 text-[#C93B3B]" strokeWidth={1.4} />
              <h3 className="font-serif text-2xl mt-6 text-[#F7ECEB]">{p.title}</h3>
              <p className="text-sm text-[#A89896] mt-3 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FOUNDER STRIP — members only */}
      {isMember && (
      <section className="bg-gradient-to-br from-[#2E1E1E] to-[#110D0D] border-y border-[#382A2A] text-white py-24" data-testid="founder-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden">
              <img src="/founder.jpg" alt="Founder" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.28em] text-[#E58B5E]">From the founder</div>
            <h2 className="font-serif text-4xl lg:text-5xl mt-6 leading-tight">
              "This work is a return — not to something new, but to something we've forgotten how to receive."
            </h2>
            <p className="mt-8 text-white/85 leading-relaxed max-w-2xl">
              I built Touche.Today for the ones who ache for closeness but haven't found a room safe enough to hold them. Every intimacy and sex wellness coaching session is held by me personally — twenty years of experience, ten of them coaching couples and individuals here in Las Vegas.
            </p>
            <Link to="/membership" className="mt-8 inline-block bg-[#F7ECEB] text-[#110D0D] px-8 py-3 rounded-full font-medium hover:bg-[#E58B5E] transition-colors" data-testid="founder-cta">
              Work with Hilda
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="font-serif text-4xl lg:text-5xl text-[#F7ECEB]">Begin gently.</h2>
        <p className="mt-6 text-[#A89896] text-lg">Create your private account. Browse practitioners. Choose the tier that fits.</p>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/register" className="btn-primary" data-testid="landing-cta-register">Create your account</Link>
          <Link to="/practitioners" className="btn-ghost" data-testid="landing-cta-browse">Browse practitioners</Link>
        </div>
      </section>
    </div>
  );
}
